<template>
  <div
    :class="{
      'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      'no-padding': !hasMediaContent,
    }"
    class="content-container"
  >
    <default-overlay
      v-if="hasMediaContent || !allButtonsHidden"
    />
    <qr-code-component
      :qr-code-data="currentContent?.attrs?.qrCode"
      :has-margin="!!(headline.value || text.value || (buttons.length > 0 && !currentContent.attrs?.invisibleButtons))"
    />
    <media-text
      v-if="headline.value?.trim().length"
      :data="headline"
      :class="{
        headline: true,
        'text-margin': !text.value
      }"
      class="media-text"
      weight="bold"
    />
    <media-text
      v-if="text.value?.trim().length"
      :data="text"
      class="text media-text"
    />
    <div
      v-if="!currentContent.attrs?.invisibleButtons"
      :style="{ backgroundColor: currentContent.styles?.background_color }"
      :class="{
        'has-avatar': !isHideAvatar || isKeepAvatarDesign,
      }"
      class="buttons-container"
    >
      <div
        v-if="!currentContent.attrs?.invisibleButtons"
        :class="{
          'has-avatar': !isHideAvatar || isKeepAvatarDesign,
        }"
        class="buttons"
      >
        <div
          v-for="({ button, next }, index ) in buttons"
          :key="index"
          :style="{
            borderRadius: `${button.styles.shape / 2}%`,
            backgroundColor: button.styles.background_color,
            backgroundImage: getBackgroundImage(button),
            opacity: selectedButton !== -1 && selectedButton !== index && !button.isHidden ? 0.6 : null,
            borderStyle: button.styles.borderStyle.type,
            borderWidth: `${button.styles.borderStyle.width}px`,
            borderColor: button.styles.borderStyle.color,
          }"
          :class="{
            [`size-${buttonSize}`]: true,
            [button.styles.vertical_align]: true,
            hidden: button.isHidden,
            'text-full-size': !button.customIconFileName && !button.styles.icon,
            'has-media-content': hasMediaContent,
          }"
          class="button"
          @click="handleNext(index, next)"
        >
          <div
            v-if="button.media.filename"
            :style="{
              background: button.styles.overlay,
              borderRadius: getOverlayBorderRadius(button.styles),
            }"
            class="button-overlay"
          />
          <div
            v-if="(button.customIconFileName || button.styles.icon) && !button.styles.hide_icon"
            :class="{
              'margin-bottom': !!button.text && !button.styles.flip_elements_vertical,
              'margin-top': !!button.text && button.styles.flip_elements_vertical,
              'flip': button.styles.flip_elements_vertical
            }"
            class="icon-wrapper"
          >
            <div
              v-if="button.customIconFileName"
              :style="{
                background: getCustomIcon(button),
              }"
              class="custom-icon"
            />
            <i
              v-else
              :style="{ color: button.styles.icon_color }"
              :class="`icon ${button.styles.icon}`"
            />
          </div>
          <div
            :style="{
              '--font-size': `${button.styles.fontSize ?? 22}px`,
              color: button.styles.text_color,
              textAlign: button.styles.text_align,
              fontFamily: `${FontLoader.buildFontFamily(button.styles.font)} !important`,
              width: button.styles.hide_icon ? `calc(100% - ${button.styles.shape / 2}px)` : '100%'
            }"
            :dir="languageDir"
            class="button-text"
          >
            {{ button.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { set } from 'lodash';
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useStt, removeEmojisFromString } from '@ht/lib-plural-vue';
import useContent from '@/composables/useContent';
import useSequence from '@/composables/useSequence';
import useChatbot from '@/composables/useChatbot';
import useAnalytics from '@/composables/useAnalytics';
import useDynamicButtons from '@/composables/useDynamicButtons';
import useDynamicUrl from '@/composables/useDynamicUrl';
import useConfig from '@/composables/useConfig';
import useConditions from '@/composables/useConditions';
import useDynamicText from '@/composables/useDynamicText';
import QrCodeComponent from '@/components/QrCodeComponent';
import DefaultOverlay from '@/components/DefaultOverlay';
import MediaText from '@/components/MediaText';
import { INTERACTION_DELAY, SYSTEM_VARIABLES_MAP, VARIABLE_VALUE_TYPES } from '@/js/constants';
import FontLoader from '@/js/fontLoader';
import { getDynamicObjects } from '@/js/dynamicData';

const BIG_SCREEN_VERTICAL = { default: 540, 2: 800 };
const BIG_SCREEN_VERTICAL_NO_AVATAR = { default: 540, 8: 800 };
const DYNAMIC_BUTTON_STYLES_MAP = {
  iconName: 'styles.icon',
  iconColor: 'styles.icon_color',
  hideIcon: 'styles.hide_icon',
  flipIcon: 'styles.flip_elements_vertical',
  backgroundColor: 'styles.background_color',
  font: 'styles.font',
  overlay: 'styles.overlay',
  borderRadius: 'styles.shape',
  textAlign: 'styles.text_align',
  verticalAlign: 'styles.vertical_align',
  borderWidth: 'styles.borderStyle.width',
  borderColor: 'styles.borderStyle.color',
  backgroundImgUrl: 'media.externalUrl',
  hideButton: 'isHidden',
};

export default defineComponent({
  name: 'MenuComponent',

  components: {
    QrCodeComponent,
    DefaultOverlay,
    MediaText,
  },

  setup() {
    const { currentContent, findContentAssetUrl, isHideAvatar, isKeepAvatarDesign, hasMediaContent } = useContent();
    const { runSequence, interruptSequence, currentSeq } = useSequence();
    const { recognizedText, sttStart, sttStop, sttPhrases } = useStt();
    const { amendAction, lastAnalyticData } = useAnalytics();
    const { setVariables, languageDir, getVariable, setFlowStateBackToStart } = useConfig();
    const { checkCondition } = useConditions();

    const headlineText = useDynamicText(currentContent.value?.attrs?.headline);
    const textText = useDynamicText(currentContent.value?.attrs?.text);

    const headline = computed(() => ({
      ...headlineText.value,
      fontSize: headlineText.value.fontSize ?? 56,
    }));

    const text = computed(() => ({
      ...textText.value,
      fontSize: textText.value.fontSize ?? 36,
    }));

    let buttons;

    if (currentContent.value.attrs.useDynamicButtons) {
      const { value: dynamicButtonsVariableValue } = getVariable(currentContent.value.attrs.dynamicButtonsVariableId)
        || {};

      try {
        const payload = typeof dynamicButtonsVariableValue === 'string' ? JSON.parse(dynamicButtonsVariableValue) : dynamicButtonsVariableValue;
        const dynamicButtons = getDynamicObjects(payload);
        buttons = computed(() => dynamicButtons.map(({ label, value, ...rest }) => {
          const options = Object.keys(rest).reduce((acc, key) => {
            if (rest[key] !== null) {
              if (key === 'iconName' && !['icon-mat-', 'icon-ele-', 'icon-hawk-', 'icon-fa-'].some((prefix) => rest[key].startsWith(prefix))) {
                // use material icons as fallback
                set(acc, DYNAMIC_BUTTON_STYLES_MAP[key], `icon-mat-${rest[key].replace(' ', '_')}`.toLowerCase());
              } else if (['hideButton', 'hideIcon'].includes(key) && typeof rest[key] === 'string') {
                set(acc, DYNAMIC_BUTTON_STYLES_MAP[key], rest[key] === 'true');
              } else {
                set(acc, DYNAMIC_BUTTON_STYLES_MAP[key], rest[key]);
              }
            }
            return acc;
          }, {});
          return {
            button: {
              text: label,
              variations: [label || ''],
              variables: [
                { id: currentContent.value.attrs.variableId, value: { type: VARIABLE_VALUE_TYPES.PRIMITIVE, value } },
              ],
              isHidden: options.isHidden,
              styles: {
                ...currentContent.value.attrs.dynamicButtonStyles,
                ...options.styles || {},
                borderStyle: {
                  ...currentContent.value.attrs.dynamicButtonStyles.borderStyle,
                  ...options.styles?.borderStyle || {},
                },
              },
              media: {
                ...currentContent.value.attrs.dynamicButtonStyles.media,
                ...options.media || {},
              },
              customIconFileName: '',
            },
            next: currentContent.value.attrs.next,
          };
        }));
      } catch (err) {
        buttons = computed(() => []);
      }
    } else {
      buttons = useDynamicButtons(currentContent.value?.attrs.items.filter(({ button }) => (button.condition
        ? checkCondition(button.condition) : true)));
    }

    const allButtonsHidden = computed(
      () => currentContent.value.attrs?.invisibleButtons || buttons.value.every((i) => i.button.isHidden),
    );

    const buttonSize = ref(0);
    const selectedButton = ref(-1); // -1 means no selected
    const backgroundOpacity = computed(() => {
      const { background_color: backgroundColor } = currentContent.value.styles;
      return parseFloat(backgroundColor.substring(
        backgroundColor.indexOf('(') + 1,
        backgroundColor.lastIndexOf(')'),
      ).split(/,\s*/)[3], 10);
    });

    // keys is a screen resolution, min and max values
    // values is a map where key is buttons count and default option if nothing match
    const resolutionSizes = computed(() => new Map([
      [[0, 679], { default: hasMediaContent.value ? 125 : 108, 1: hasMediaContent.value ? 220 : 200 }],
      [[680, 1080], {
        default: hasMediaContent.value ? 220 : 200,
        2: 240,
        3: hasMediaContent.value ? 220 : 200,
        4: 240,
      }],
      [[1081, 1439], { default: 162, 2: 240, 3: 162, 4: 240 }],
      [[1440, 1920], { default: 240, 2: 300, 3: 240, 4: 300 }],
      [[1921, 2560], { default: 300, 2: 400, 3: 300, 4: 400 }],
      [[2561, 3839], { default: 400, 2: 540, 3: 400, 4: 540 }],
      [[3840], { default: 540, 2: 800, 3: 540, 4: 800 }],
    ]));

    const resolutionNoAvatarSizes = computed(() => new Map([
      [[0, 679], { default: hasMediaContent.value ? 125 : 108, 3: hasMediaContent.value ? 220 : 200 }],
      [[680, 1080], { default: hasMediaContent.value ? 220 : 200, 6: 240 }],
      [[1081, 1439], { default: hasMediaContent.value ? 220 : 200, 4: 240 }],
      [[1440, 1920], { default: 240, 6: 300 }],
      [[1921, 2560], { default: 300, 4: 400 }],
      [[2561, 3839], { default: 400, 4: 540 }],
      [[3840], { default: 540, 6: 800 }],
    ]));

    function getButtonSize() {
      const { clientWidth: windowWidth, clientHeight: windowHeight } = document.documentElement;
      let sizes;

      if (windowHeight <= 480) {
        if (isHideAvatar.value || isKeepAvatarDesign.value) {
          sizes = { default: hasMediaContent.value ? 125 : 108, 3: hasMediaContent.value ? 220 : 200 };
        } else {
          buttonSize.value = hasMediaContent.value ? 125 : 108;
          return;
        }
      }

      if (windowHeight >= 3840) {
        sizes = isHideAvatar.value || isKeepAvatarDesign.value ? BIG_SCREEN_VERTICAL_NO_AVATAR : BIG_SCREEN_VERTICAL;
      } else if (windowHeight > 480) {
        const sizesMap = isHideAvatar.value || isKeepAvatarDesign.value
          ? resolutionNoAvatarSizes.value
          : resolutionSizes.value;
        sizesMap.forEach((value, breakpoints) => {
          const [min, max] = breakpoints;
          if (max) {
            if (windowWidth >= min && windowWidth <= max) {
              sizes = value;
            }
          } else if (windowWidth >= min) {
            sizes = value;
          }
        });
      }

      if (sizes[buttons.value.length]) {
        buttonSize.value = sizes[buttons.value.length];
        return;
      }

      let size = 0;
      Object.keys(sizes).forEach((buttonsLength) => {
        if (buttonsLength === 'default') {
          return;
        }
        const length = parseInt(buttonsLength, 10);
        if (buttons.value.length >= size && buttons.value.length <= length) {
          size = length;
        }
      }, 0);

      buttonSize.value = sizes[size] || sizes.default;
    }

    function handleNext(index, nextId) {
      selectedButton.value = index;

      if (buttons.value[index].button.variables) {
        setVariables(buttons.value[index].button.variables);
      }

      amendAction({
        menuButtonSelected: buttons.value[index].button.text,
      });

      setTimeout(() => {
        if (nextId) {
          interruptSequence();
          setFlowStateBackToStart(nextId);
          runSequence(nextId);
        }
      }, INTERACTION_DELAY);
    }

    function getBackgroundImage(button) {
      if (button.media.externalUrl) {
        return `url(${useDynamicUrl(button.media.externalUrl)})`;
      }

      if (!button.media.filename) {
        return null;
      }

      return `url(${findContentAssetUrl(button.media.filename)})`;
    }

    function getCustomIcon(button) {
      return button.customIconFileName
        ? `url(${findContentAssetUrl(button.customIconFileName)})`
        : '';
    }

    function getOverlayBorderRadius({ shape, borderStyle: { width } }) {
      const outerRadiusPx = (shape / 2 / 100) * buttonSize.value;
      const innerRadiusPx = outerRadiusPx - width;
      const innerWidthPx = buttonSize.value - width * 2;
      return innerRadiusPx > 0 ? `${(innerRadiusPx * 100) / innerWidthPx}%` : 0;
    }

    const { askChatbot } = useChatbot();
    watch(recognizedText, async (value) => {
      const speechText = value.text.trim();
      if (speechText && selectedButton.value === -1 && value.isFinal) {
        const index = buttons.value.findIndex(({ button }) => button.variations
          .some(
            (v) => v?.length && speechText.toLowerCase().includes(
              removeEmojisFromString(v).toLowerCase().replace('\n', ' '),
            ) && !button.isHidden,
          ));

        if (index !== -1) {
          handleNext(index, buttons.value[index].next);
        } else if (currentContent.value.attrs.usePluralDialogs) {
          await askChatbot(speechText);
        } else if (currentContent.value.attrs.anythingElse) {
          const next = currentContent.value.attrs.anythingElse;
          interruptSequence();
          setFlowStateBackToStart(next);
          runSequence(next);
        }
      }
    });

    onMounted(() => {
      setVariables([{
        id: SYSTEM_VARIABLES_MAP.CURRENT_DESIGN_ELEMENT.id,
        value: { value: currentSeq.value.id },
      }]);
      sttPhrases(buttons.value
        .map(({ button }) => ({ variations: button.variations.filter((v) => v?.length) }))
        .filter(({ variations }) => variations.length));
      getButtonSize();
      window.addEventListener('resize', getButtonSize);
      sttStart({
        sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
        elementId: lastAnalyticData.value.elementId,
      });
    });

    onBeforeUnmount(() => {
      sttStop();
      window.removeEventListener('resize', getButtonSize);
    });

    return {
      headline,
      text,
      hasMediaContent,
      currentContent,
      isHideAvatar,
      isKeepAvatarDesign,
      buttons,
      buttonSize,
      selectedButton,
      allButtonsHidden,
      handleNext,
      getBackgroundImage,
      getOverlayBorderRadius,
      getCustomIcon,
      backgroundOpacity,
      FontLoader,
      languageDir,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "sass:math";
@import "@/scss/common-ui.scss";

.content-container {
  @extend .content-container;
}

.buttons-container {
  @extend .buttons-container;

  &.has-avatar {
    @media (orientation: portrait) {
      max-height: clamp(320px, 37vh, 700px);
    }
  }
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  gap: clamp(10px, 2.1vmin, 22px);
  -webkit-overflow-scrolling: touch;
  will-change: transform;
  transform: translateZ(0);
}

.button {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 500;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  flex-shrink: 0;
  position: relative;
  margin: 0;
  line-height: 1.625; // 26px/16px = 1.625 line height ratio
  width: clamp(125px, 25vmin, 260px);
  height: clamp(125px, 25vmin, 260px);
  padding: clamp(8px, 1.2vmin, 10px);
  overflow: hidden;

  .custom-icon {
    width: clamp(32px, 7vmin, 60px);
    height: clamp(32px, 7vmin, 60px);
  }

  .icon {
    font-size: clamp(32px, 7vmin, 60px);
  }

  .margin-top {
    margin-top: clamp(8px, 1.2vmin, 10px);
  }

  .margin-bottom {
    margin-bottom: clamp(8px, 1.2vmin, 10px);
  }

  .custom-icon {
    background-size: contain !important;
  }

  &.hidden {
    opacity: 0;
    filter: none !important;
    box-shadow: none !important;
    pointer-events: none;
  }

  &.top {
    justify-content: flex-start;
  }

  &.center {
    justify-content: center;
  }

  &.bottom {
    justify-content: flex-end;
  }

  .button-text {
    @include font-size-responsive();
  }

  &.text-full-size {
      .button-text {
      max-height: clamp(110px, 40vmin, 230px);
      @media (orientation: portrait) {
        @media (max-width: $mobile_standard_breakpoint) {
          -webkit-line-clamp: 7;
        }

        @media (min-width: $mobile_standard_breakpoint + 1px) and (max-width: $tablet_standard_breakpoint) {
          -webkit-line-clamp: 8;
        }

        @media (min-width: $tablet_standard_breakpoint + 1px) {
          -webkit-line-clamp: 8;
        }
      }

      @media (orientation: landscape) {
        @media (max-height: $mobile_standard_breakpoint) {
          -webkit-line-clamp: 7;
        }

        @media (min-height: $mobile_standard_breakpoint + 1px) and (max-height: $tablet_standard_breakpoint) {
          -webkit-line-clamp: 8;
        }

        @media (min-height: $tablet_standard_breakpoint + 1px) {
          -webkit-line-clamp: 8;
        }
      }
    }
  }
}

.button-text {
    max-height: clamp(110px, 40vmin, 140px);

    @media (orientation: portrait) {
      @media (max-width: $mobile_standard_breakpoint) {
        -webkit-line-clamp: 4;
      }

      @media (min-width: $mobile_standard_breakpoint + 1px) and (max-width: $tablet_standard_breakpoint) {
        -webkit-line-clamp: 5;
      }

      @media (min-width: $tablet_standard_breakpoint + 1px) {
        -webkit-line-clamp: 5;
      }
    }

    @media (orientation: landscape) {
      @media (max-height: $mobile_standard_breakpoint) {
        -webkit-line-clamp: 4;
      }

    @media (min-height: $mobile_standard_breakpoint + 1px) and (max-height: $tablet_standard_breakpoint) {
      -webkit-line-clamp: 5;
    }

    @media (min-height: $tablet_standard_breakpoint + 1px) {
      -webkit-line-clamp: 5;
    }
  }
}

.icon-wrapper {
  z-index: 1;

  &.flip {
    order: 2;
  }
}

.button-overlay {
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
}
</style>
